/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { getDecimalDataSeriesValues, getEnumDataSeriesCount } from './PlotterTools.js';
import { isColor, toCssColorValue } from '../_utils/ColorTools.js';
import { defaultShorthandLabels } from '../_utils/NumberFormatter.js';
import { createTicksFormat, createTicksFormatMinMax } from '../_utils/TicksFormat.js';
import { computeThresholds } from '../_utils/Thresholds.js';
const DEFAULT_ENUM_CONFIG = {
  values: Array.from({
    length: 16
  }, (_, index) => {
    return {
      ordinal: index,
      label: index.toString(10),
      stripeStyle: {
        backgroundColor: index
      }
    };
  })
};
const DEFAULT_ENUM_VALUES_LENGTH = DEFAULT_ENUM_CONFIG.values.length;
/**
 * Computes the enum data-series from the given configurations and sample.
 *
 * @param enumDataSeriesConfigs - the configuration of the enum data-series
 * @param enumLegendLabels - labels defined for the legend of the plotter
 * @param sample - a sample that potentially contains enum-values
 */

export function computeEnumDataSeries(enumDataSeriesConfigs, enumLegendLabels, sample) {
  const enumDataSeriesCountFromSample = sample === undefined ? 0 : getEnumDataSeriesCount(sample);
  const enumDataSeriesCountFromConfigs = enumDataSeriesConfigs.length;
  const enumDataSeriesCountMax = Math.max(enumDataSeriesCountFromSample, enumDataSeriesCountFromConfigs);
  return Array.from({
    length: enumDataSeriesCountMax
  }, (_, index) => toEnumDataSeries(index, enumDataSeriesConfigs[index], enumLegendLabels[index]));
}

function toEnumDataSeries(enumSeriesIndex, config, legendLabel) {
  var _ref, _config$label;

  return {
    id: enumSeriesIndex,
    name: (_ref = (_config$label = config === null || config === void 0 ? void 0 : config.label) !== null && _config$label !== void 0 ? _config$label : legendLabel) !== null && _ref !== void 0 ? _ref : '',
    enumConfiguration: {
      description: `Enm-${enumSeriesIndex}`,
      values: toEnumValues(config)
    }
  };
}

function toEnumValues(config) {
  var _config$enumConfig$va, _config$enumConfig;

  const enumValues = ((_config$enumConfig$va = config === null || config === void 0 ? void 0 : (_config$enumConfig = config.enumConfig) === null || _config$enumConfig === void 0 ? void 0 : _config$enumConfig.values) !== null && _config$enumConfig$va !== void 0 ? _config$enumConfig$va : DEFAULT_ENUM_CONFIG.values).map(value => {
    var _value$label;

    return {
      ordinal: value.ordinal,
      label: (_value$label = value.label) !== null && _value$label !== void 0 ? _value$label : ''
    };
  });

  if (enumValues.length === DEFAULT_ENUM_VALUES_LENGTH) {
    return enumValues;
  }

  return processEnumValues(enumValues, v => v, o => ({
    ordinal: o,
    label: `<${o}>`
  }));
}
/**
 * Computes the decimal data-series from the given sample.
 *
 * @param sample - a sample that potentially contains decimal values
 */


export function computeDecimalDataSeriesFromSample(sample) {
  // The sample determines the number of the data series stored into the history.
  // Each data series has an id and a name which will be stored into the history, too.
  // Currently, the id is just the index of the data series and the name is left blank.
  // However, as soon as the client wants to access the history directly or wants to export
  // the history the id and the name may become very useful.
  return getDecimalDataSeriesValues(sample).map((_, index) => {
    return {
      id: index,
      name: ''
    };
  });
}
/**
 * Computes the line styles.
 *
 * @param dataSeriesConfigs - the configuration of the data-series
 * @param palette - the palette to be used to resolve colors
 */

export function computeLineStyles(dataSeriesConfigs, palette) {
  const lineStyles = new Array();

  for (let lineIndex = 0; lineIndex < Math.max(palette.length, dataSeriesConfigs.length); lineIndex++) {
    var _dataSeriesConfigs$li;

    const lineColor = (_dataSeriesConfigs$li = dataSeriesConfigs[lineIndex]) === null || _dataSeriesConfigs$li === void 0 ? void 0 : _dataSeriesConfigs$li.lineColor;
    lineStyles.push({
      lineStyle: {
        color: toCssColorValue(isColor(lineColor) ? lineColor : lineIndex, palette)
      }
    });
  }

  return lineStyles;
}
/**
 * Compute decimal data-series styles from data-series configurations.
 *
 * @param dataSeriesConfigs - data-series configurations
 * @param hostFontFamily - the font-family used for the host-element
 * @param lineStyles - the styles used for the lines of the data-series
 */

export function computeDecimalDataSeriesStyles(dataSeriesConfigs, hostFontFamily, lineStyles) {
  return dataSeriesConfigs.map((config, index) => toDecimalDataSeriesStyle(config, hostFontFamily, lineStyles[index % lineStyles.length].lineStyle));
}

function toDecimalDataSeriesStyle(config, hostFontFamily, lineStyle) {
  var _config$shorthandLabe;

  const dataSeriesConfiguration = {};
  dataSeriesConfiguration.valueAxisTitle = config.unit;
  dataSeriesConfiguration.valueRange = config.valueRange;
  const shorthandLabels = (_config$shorthandLabe = config.shorthandLabels) !== null && _config$shorthandLabe !== void 0 ? _config$shorthandLabe : defaultShorthandLabels;
  const decimalPlaces = config.decimalPlaces;
  dataSeriesConfiguration.ticksFormat = decimalPlaces === undefined ? createTicksFormatMinMax(shorthandLabels, 0, 4) : createTicksFormat(shorthandLabels, decimalPlaces);
  dataSeriesConfiguration.thresholds = computeDataSeriesThresholds(config, hostFontFamily, lineStyle);
  return dataSeriesConfiguration;
}

function computeDataSeriesThresholds(config, fontFamily, lineStyle) {
  const thresholds = config.thresholds;

  if (thresholds === undefined) {
    return null;
  }

  return computeThresholds(thresholds, true, fontFamily, config.shorthandLabels, config.decimalPlaces, lineStyle.color);
}
/**
 * Compute enum data-series styles from enum data-series configurations.
 *
 * @param enumDataSeriesConfigs - enum data-series configurations
 * @param palette - the palette to compute the colors for
 * @param enumDataSeriesCount - the number of enum data-series
 */


export function computeEnumDataSeriesStyles(enumDataSeriesConfigs, palette, enumDataSeriesCount) {
  const styles = enumDataSeriesConfigs.map(config => toEnumDataSeriesStyle(config, palette));

  if (styles.length < enumDataSeriesCount) {
    const defaultEnumDataSeriesStyle = toEnumDataSeriesStyle({
      enumConfig: DEFAULT_ENUM_CONFIG
    }, palette);

    while (styles.length < enumDataSeriesCount) {
      styles.push(defaultEnumDataSeriesStyle);
    }
  }

  return styles;
}

function toEnumDataSeriesStyle(config, palette) {
  return {
    aggregationMode: config.aggregationMode,
    stripeStyles: toStripeStyles(config.enumConfig.values, palette)
  };
}

function toStripeStyles(enumValues, palette) {
  return processEnumValues(enumValues, v => toStripeStyle(v.ordinal, v.stripeStyle, palette), o => ({
    ordinal: o
  }));
}

function toStripeStyle(ordinal, stripeStyle = undefined, palette) {
  if (stripeStyle === undefined) {
    return {
      backgroundColor: toCssColorValue(ordinal, palette),
      borderColor: toCssColorValue(0, 'secondary')
    };
  }

  return {
    backgroundColor: stripeStyle.backgroundColor === undefined ? null : toCssColorValue(stripeStyle.backgroundColor, palette),
    alternateColor: stripeStyle.alternateColor === undefined ? null : toCssColorValue(stripeStyle.alternateColor, palette),
    borderColor: stripeStyle.borderColor === undefined ? null : toCssColorValue(stripeStyle.borderColor, palette),
    fill: 'Solid' // only 'solid' so far

  };
}

function processEnumValues(source, consumer, creator) {
  const target = []; // DAVIDE-540: we need to sort the source first or else duplicate ordinals will be created.

  source.sort((a, b) => a.ordinal - b.ordinal).forEach(v => {
    while (target.length < v.ordinal && v.ordinal < DEFAULT_ENUM_VALUES_LENGTH) {
      target.push(consumer(creator(target.length)));
    }

    if (v.ordinal === target.length) {
      target.push(consumer(v));
    } else {
      // eslint-disable-next-line
      console.warn(`value with ordinal <${v.ordinal}> will be skipped`, v);
    }
  });

  while (target.length < DEFAULT_ENUM_VALUES_LENGTH) {
    target.push(consumer(creator(target.length)));
  }

  return target;
}
